<template>
  <div>
    <vue-element-loading :active="isLoading" is-full-screen />

    <div class="container-fluid main-container pt-2">
      <ArchiveSummarySalesFilter
        v-model="filter"
        :disabled-csv-download="isLoading"
        @click-csv-download="handleClickCsvDownload"
        @get-search="submitSearch"
      />

      <div class="table-container">
        <ArchiveSummarySalesTable
          v-model="options"
          :inventories="inventories"
          :totals="totals"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { format, parse } from 'date-fns'
import VueElementLoading from 'vue-element-loading'
import { saveAs } from 'file-saver'

import ArchiveSummarySalesFilter from '@/components/archive_summary_sales/ArchiveSummarySalesFilter'
import ArchiveSummarySalesTable from '@/components/archive_summary_sales/ArchiveSummarySalesTable'

export default {
  name: 'ArchiveSummarySales',

  components: {
    VueElementLoading,
    ArchiveSummarySalesFilter,
    ArchiveSummarySalesTable
  },

  data () {
    return {
      filter: {},
      totals: {},
      inventories: [],
      options: {},
      isLoading: false
    }
  },

  watch: {
    'options.page': {
      handler: 'submitSearch',
      deep: true
    }
  },

  methods: {
    parseStringToDate (str) {
      return format(parse(str, 'yyyy/MM/dd', new Date()), 'yyyy-MM-dd')
    },
    async fetchTableData (params) {
      const {
        condition,
        dateRange,
        unit1,
        unit2,
        selectedDetailUnit1,
        selectedDetailUnit2,
        page,
        itemsPerPage,
        displayAll
      } = params
      const [startAt, endAt] = dateRange
      const offset = (page - 1) * itemsPerPage
      const limit = itemsPerPage

      const payload = {
        condition,
        start_at: startAt ? this.parseStringToDate(startAt) : '',
        end_at: endAt ? this.parseStringToDate(endAt) : '',
        unit_1: unit1.key,
        unit_2: unit2.key,
        limit,
        offset,
        display_all: displayAll,
        ...(selectedDetailUnit1 ? { search_1: selectedDetailUnit1 } : null),
        ...(selectedDetailUnit2 ? { search_2: selectedDetailUnit2 } : null)
      }

      try {
        this.isLoading = true
        const {
          data: { status, data, total }
        } = await this.$http.post(this.$api_url.detail, payload)
        if (status) {
          this.totals = total
          this.inventories = data
        } else {
          throw new Error('Something wrong!')
        }
      } catch (error) {
        this.$toastr.error(
          'エラーのため失敗しました。ご確認後、再試行してください。',
          'エラー'
        )
        this.totals = {}
        this.inventories = []
      } finally {
        this.isLoading = false
      }
    },
    async handleClickCsvDownload () {
      const { condition, dateRange, unit1, unit2, selectedDetailUnit1, selectedDetailUnit2 } = this.filter
      const [startAt, endAt] = dateRange

      const payload = {
        condition,
        start_at: startAt ? this.parseStringToDate(startAt) : '',
        end_at: endAt ? this.parseStringToDate(endAt) : '',
        unit_1: unit1.key,
        unit_2: unit2.key,
        ...(selectedDetailUnit1 ? { search_1: selectedDetailUnit1 } : null),
        ...(selectedDetailUnit2 ? { search_2: selectedDetailUnit2 } : null)
      }

      try {
        this.isLoading = true
        let { data } = await this.$http.post(
          this.$api_url.dashboard_detail_csv_download,
          payload
        )
        await this.createCSV(data)
      } catch (error) {
        this.$toastr.error(
          'エラーのため失敗しました。ご確認後、再試行してください。',
          'エラー'
        )
      } finally {
        this.isLoading = false
      }
    },

    getTotalDataCsv (totalHeader, totalData) {
      const header = [
        '',
        '',
        '',
        '',
        '',
        '',
        totalHeader.revenue_amount,
        totalHeader.revenue_quantity,
        totalHeader.return_amount,
        totalHeader.return_quantity,
        totalHeader.return_rate,
        totalHeader.discount_amount,
        totalHeader.profit_amount,
        totalHeader.profit_rate
      ]

      const data = [
        '',
        '',
        '',
        '',
        '',
        '',
        this.$util.formatToLocaleString(totalData.revenue_amount),
        this.$util.formatToLocaleString(totalData.revenue_quantity),
        this.$util.formatToLocaleString(totalData.return_amount),
        this.$util.formatToLocaleString(totalData.return_quantity),
        this.$util.formatToLocaleString(totalData.return_rate) + '%',
        this.$util.formatToLocaleString(totalData.discount_amount),
        this.$util.formatToLocaleString(totalData.profit_amount),
        this.$util.formatToLocaleString(totalData.profit_rate) + '%'
      ]

      return [header, data]
    },

    getTableDataCsv (tableHeader, tableData) {
      const header = [
        tableHeader.jan_code,
        tableHeader.model,
        tableHeader.customer,
        tableHeader.supplier,
        tableHeader.category,
        tableHeader.employee,
        tableHeader.revenue_amount,
        tableHeader.revenue_quantity,
        tableHeader.return_amount,
        tableHeader.return_quantity,
        tableHeader.return_rate,
        tableHeader.discount_amount,
        tableHeader.profit_amount,
        tableHeader.profit_rate
      ]

      const data = tableData.map((record) => [
        record.jan_code,
        record.model,
        record.customer,
        record.supplier,
        record.category,
        record.employee,
        this.$util.formatToLocaleString(record.revenue_amount),
        this.$util.formatToLocaleString(record.revenue_quantity),
        this.$util.formatToLocaleString(record.return_amount),
        this.$util.formatToLocaleString(record.return_quantity),
        this.$util.formatToLocaleString(record.return_rate) + '%',
        this.$util.formatToLocaleString(record.discount_amount),
        this.$util.formatToLocaleString(record.profit_amount),
        this.$util.formatToLocaleString(record.profit_rate) + '%'
      ])

      return [header, ...data]
    },

    createCSV (data) {
      const {
        data: { total: totalData, data: tableData = [] },
        header: { total: totalHeader, table: tableHeader }
      } = data
      const tableDataCsv = this.getTableDataCsv(tableHeader, tableData)
      const totalDataCsv = this.getTotalDataCsv(totalHeader, totalData)
      const content = this.$util.convertCharacterEncodingBeforeCreateCsv([
        ...totalDataCsv,
        ...tableDataCsv
      ])
      const fileName = this.$util.createFileName('売上粗利実績明細')

      saveAs(content, fileName)
    },

    submitSearch (search) {
      if (search) {
        this.options.itemsPerPage = this.filter.displayAll === true ? -1 : 10
        this.options.page = this.filter.displayAll === true ? 1 : this.options.page
        let params = {
          ...this.filter,
          page: this.options.page || 1,
          itemsPerPage: this.options.itemsPerPage || 10
        }
        this.fetchTableData(params)
      }
    }
  }
}
</script>

<style scoped>
.table-container {
  z-index: 0;
}
</style>
